import React, { useCallback, useEffect, useRef, useState } from "react";

import { Form, Input } from "antd";
import axios, { isAxiosError } from "axios";
import { Col, Row } from "react-bootstrap";
import { FileRejection } from "react-dropzone";
import toast from "react-hot-toast";
import { useDispatch, useSelector } from "react-redux";

import { RootState } from "@/app/store";

import FileUploadDropZone from "@/common/FileUploadDropZone/FileUploadDropZone";
import FormItem from "@/common/FormItem/FormItem";
import ProgressBarItem from "@/common/ProgressBarItem/ProgressBarItem";

import {
  releasesApi,
  useCreateReleaseMutation,
  useGetSignedUploadUrlMutation,
} from "@/features/releases/releasesApi";
import { ErrorMessage } from "@/features/releases/types";
import {
  RELEASE_NOTES_MAX_LENGTH,
  releaseFileNameInputRules,
  releaseNotesInputRules,
  releaseTypeMaxBytes,
  releaseVersionInputRules,
} from "@/features/ui/constants";
import {
  ActiveModalNameEnum,
  BrandSoftwareTypeOption,
  ReleaseEnvironmentType,
} from "@/features/ui/types";

import { mbToBytes } from "@/utils/helpers";

import Select from "@/common/Select/Select";

import Icon from "@/assets/images/icons";
import ButtonSmall from "@/common/ButtonSmall/ButtonSmall";
import styles from "./ReleasesControlPanel.module.scss";

export interface InitialFormValues {
  filename: File | null;
  description: string;
  version: string;
  environment: ReleaseEnvironmentType;
}
interface CreateReleaseModalProps {
  selectedOption: BrandSoftwareTypeOption;
  onSuccess: () => void;
}

const initialFormValues: InitialFormValues = {
  filename: null,
  description: "",
  version: "",
  environment: "staging",
};

const { TextArea } = Input;

const ReleasesControlPanel: React.FC<CreateReleaseModalProps> = ({
  selectedOption,
  onSuccess,
}) => {
  const dispatch = useDispatch();

  const activeModalState = useSelector(
    (state: RootState) => state.ui.activeModalState
  );

  const [uploadProgress, setUploadProgress] = useState<number | null>(null);

  const controller = useRef(new AbortController());

  const [createRelease, { isLoading: isCreatingRelease }] =
    useCreateReleaseMutation();
  const [getSignedUploadUrl] = useGetSignedUploadUrlMutation();

  const [acceptedFileAPK, setAcceptedFileAPK] = useState<File | null>(null);

  const [form] = Form.useForm<InitialFormValues>();

  const { name: activeModalName } = activeModalState;

  const show = activeModalName === ActiveModalNameEnum.Release;

  useEffect(() => {
    if (show && selectedOption !== null) {
      form.resetFields();
      setAcceptedFileAPK(null);
    }
  }, [form, show, selectedOption]);

  const handleOnDropFile = useCallback(
    (acceptedFiles: File[]) => {
      if (acceptedFiles.length === 0) return;

      form.setFieldValue("filename", acceptedFiles[0]);

      setAcceptedFileAPK(acceptedFiles[0]);
    },
    [form]
  );

  const handleOnDropRejected = (rejectedFiles: FileRejection[]) => {
    const rejectedFile = rejectedFiles[0];

    form.setFields([
      {
        name: "filename",
        errors: [
          `Rejected file: ${rejectedFile.file.name} - ${rejectedFile.errors[0].message}`,
        ],
      },
    ]);
  };

  const handleSubmit = async (values: InitialFormValues) => {
    if (!values.filename) return;

    if (acceptedFileAPK === null) {
      throw new Error("APK file is missing");
    }

    setUploadProgress(0);

    const createReleaseBody = {
      version: values.version,
      description: values.description,
      environment: values.environment,
      filename: acceptedFileAPK.name,
      type: selectedOption.value,
    };

    try {
      const signed = await getSignedUploadUrl({
        filename: acceptedFileAPK.name,
      }).unwrap();

      await axios.put(signed.url, acceptedFileAPK, {
        headers: {
          "Content-Type": "application/vnd.android.package-archive",
        },
        signal: controller.current.signal,
        onUploadProgress: (progressEvent) => {
          const { total, loaded } = progressEvent;
          if (total !== null && total !== undefined) {
            const progress = Math.round((loaded * 100) / total);
            setUploadProgress(progress);
          }
        },
      });

      await createRelease(createReleaseBody).unwrap();

      dispatch(releasesApi.util.invalidateTags(["Releases"]));

      toast.success("APK uploaded and release metadata created");

      onSuccess();
    } catch (error) {
      if (isAxiosError<ErrorMessage>(error)) {
        if (error.code === "ERR_CANCELED") {
          toast.error("Upload canceled");
        } else {
          const message = error.response?.data.message;
          toast.error(
            `Failed to upload APK ${message ?? ""}. Please try again.`
          );
        }
      } else {
        const message =
          (error as any)?.data?.message ||
          (error as Error)?.message ||
          "Failed to upload APK. Please try again.";
        toast.error(String(message));
      }
    } finally {
      setUploadProgress(null);
      controller.current = new AbortController();
    }
  };

  const maxFileSize = releaseTypeMaxBytes[selectedOption.value];

  const handleOnCloseDownload = () => {
    controller.current.abort();
    setAcceptedFileAPK(null);
  };

  return (
    <Form
      requiredMark={false}
      layout="vertical"
      onFinish={handleSubmit}
      initialValues={initialFormValues}
      form={form}
    >
      <div className="d-flex justify-content-between mb-3">
        <span className={styles.title}>
          Upload the APK for the {selectedOption.label} below.
        </span>
        <Icon size={25} name={selectedOption.value} useImage={true} />
      </div>
      <Row className="d-flex">
        <Col sm={12}>
          <Form.Item
            label="Version"
            name="version"
            rules={releaseVersionInputRules}
          >
            <Input placeholder="Enter version number" size="large" />
          </Form.Item>
          <FormItem
            label="Release Notes"
            name="description"
            rules={releaseNotesInputRules}
          >
            <TextArea
              maxLength={RELEASE_NOTES_MAX_LENGTH}
              showCount
              rows={6}
              className={styles.description}
              placeholder={`Max length ${RELEASE_NOTES_MAX_LENGTH} characters`}
            />
          </FormItem>
          <Form.Item name="environment" label="Staging or Production">
            <Select
              options={[
                { value: "staging", label: "Staging" },
                { value: "production", label: "Production" },
              ]}
            />
          </Form.Item>
          <Form.Item
            style={{ margin: 0 }}
            label="Upload APK"
            shouldUpdate={true}
          >
            {({ getFieldError }) => (
              <FormItem name="filename" rules={releaseFileNameInputRules}>
                <FileUploadDropZone
                  maxSize={mbToBytes(maxFileSize)}
                  onDrop={handleOnDropFile}
                  onDropRejected={handleOnDropRejected}
                  error={getFieldError("filename")[0] || ""}
                  fileName={acceptedFileAPK?.name}
                />
              </FormItem>
            )}
          </Form.Item>
          {uploadProgress !== null && acceptedFileAPK !== null && (
            <ProgressBarItem
              onClose={handleOnCloseDownload}
              fileName={acceptedFileAPK.name}
              progress={uploadProgress}
              fileSize={acceptedFileAPK.size}
            />
          )}
          <Row>
            <Col sm={12} className="d-flex justify-content-end">
              <ButtonSmall
                type="submit"
                disabled={isCreatingRelease || uploadProgress !== null}
              >
                Upload and Create
              </ButtonSmall>
            </Col>
          </Row>
        </Col>
      </Row>
    </Form>
  );
};

export default ReleasesControlPanel;
